import dayjs from 'dayjs';
import type Highcharts from 'highcharts';
import { isDate } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useGetRadarChartDefaultOptions } from 'src/components/highcharts';
import { HighchartsWidget } from 'src/pages/dashboards/HighchartsWidget';
import { useDashboardStore } from 'src/pages/dashboards/useDashboardStore';

import { NoWidgetData } from '../../../widgets/NoWidgetData';
import { WidgetLoading } from '../../../widgets/widget-loading';
import { useAggregateCategories } from '../../hooks/useAggregateCategories';
import { useGetWidgetData } from '../../hooks/useGetWidgetData';
import { useXDomain } from '../../hooks/useXDomain';
import type { CategoryType, WidgetDataSource } from '../../types';
import { getAggregatedValueFormatter } from '../../util/aggregatedValueFormatter';
import { resolveClickedSubCategory } from '../../util/resolveClickedSubCategory';
import type { GigaRadarWidgetProps } from './GigaRadarWidgetProps';
import { useRadarDataSeries } from './hooks/useRadarDataSeries';

export const GigaRadarWidget = <
  TDataSource extends WidgetDataSource,
  TCategory extends CategoryType,
  TSubCategory extends CategoryType,
>({
  dataSource,
  variables,
  categoryGetter,
  dateFilterOptions,
  subCategoryGetter,
  subCategoryRatingTranslationKey,
  onClickUrl,
  categoryRatingTranslationKey,
  propertyFilterQuery,
  aggregationType,
  aggregationField,
  categoryOverrideFunction,
  subCategoryOverrideFunction,
}: GigaRadarWidgetProps<TDataSource, TCategory, TSubCategory>) => {
  const { filters } = useDashboardStore();

  const {
    tableProps: { allItems, columnDefinitions },
    loading,
  } = useGetWidgetData({
    dataSource,
    variables,
    dateFilterOptions,
    propertyFilterQuery,
  });

  const valueFormatter = getAggregatedValueFormatter(
    columnDefinitions,
    aggregationType,
    aggregationField
  );

  const navigate = useNavigate();

  const countedCategories = useAggregateCategories({
    dataSource,
    items: allItems,
    dateFilterOptions,
    categoryGetter,
    subCategoryGetter,
    aggregationType,
    aggregationField,
  });

  const xDomain = useXDomain(countedCategories, dateFilterOptions);

  const dataSeries = useRadarDataSeries(countedCategories, {
    entityNamePlural: dataSource.entityNamePlural,
    category: categoryRatingTranslationKey,
    subCategory: subCategoryRatingTranslationKey,
    categoryOverrideFunction: categoryOverrideFunction,
    subCategoryOverrideFunction: subCategoryOverrideFunction,
  });

  const { t } = useTranslation();

  const onClick = (event: Highcharts.SeriesClickEventObject) => {
    const category = countedCategories.find(
      (c) => c.label === (event.point.category as string)
    );
    if (!category) {
      return;
    }
    const subCategory = resolveClickedSubCategory(category, event);
    const url = onClickUrl?.(category, filters, subCategory);
    if (url) {
      navigate(url);
    }
  };

  const enableLegend = useMemo(
    () => Boolean(subCategoryGetter),
    [subCategoryGetter]
  );

  const defaultOptions = useGetRadarChartDefaultOptions({
    subtitle: `No. of ${t(dataSource.entityNamePlural)}`,
    onClick,
    enableLegend,
    valueFormatter,
  });

  if (loading || !allItems) {
    return <WidgetLoading />;
  }

  if (dataSeries.length === 0) {
    return <NoWidgetData />;
  }

  const options: Highcharts.Options = {
    ...defaultOptions,
    xAxis: {
      ...defaultOptions.xAxis,
      categories:
        xDomain?.map((d) =>
          isDate(d) ? dayjs(d).format(dateFilterOptions?.dateFormat) : String(d)
        ) ?? dataSeries[0].data.map((d) => String(d.x)),
    },
    series: dataSeries.reverse().map((s) => ({
      name: s.title,
      data: xDomain
        ? xDomain.map((d) => ({
            y: s.data.find((sd) => String(sd.x) === String(d))?.y ?? 0,
          }))
        : s.data.map((d) => ({ y: d.y, color: d.color })),
      color: s.color,
      type: 'column',
      // Threaded so the click handler can resolve the clicked segment back to
      // its subcategory for clickthrough filtering (RSP-6045).
      custom: { subCategoryKey: s.subCategoryKey },
    })) as Highcharts.SeriesOptionsType[],
  };

  return <HighchartsWidget options={options} />;
};
